import { useRecoilState, useSetRecoilState } from "recoil";
import { overlayState, toastState } from "atoms";

export const formatPriceString = (priceString) => {
  if (priceString && typeof priceString === "string" && priceString !== "") {
    if (priceString[0] === "-") {
      return `- $${priceString.substring(1)}`;
    } else if (!isNaN(priceString[0], 10)) {
      return `$${priceString}`;
    } else {
      return priceString;
    }
  } else {
    return "";
  }
};

export const isOneTimeChargeItem = (serviceOption) => {
  const status = serviceOption.status;
  const qty = serviceOption.selectedQuantity;
  const frequency = serviceOption.frequency;
  return ("Optional" === status || "Included" === status) && 0 < qty && "N" === frequency;
};

export const formatQueryStringWithServices = (string) => {
  const params = new URLSearchParams(string);
  const services = params.getAll("services");
  const formattedString = services.length > 1 ? `${services[0]}&services=${services.slice(1).join("&services=")}` : services[0];
  return formattedString;
};

export const setSpeeds = (html, card) => {
  let modifiedText = html;
  if (html.includes("{{speed")) {
    modifiedText = html
      .replace("{{speedDown}}", card.speedDown)
      .replace("{{speedDownUnits}}", card.speedDownUnits)
      .replace("{{speedUp}}", card.speedUp)
      .replace("{{speedUpUnits}}", card.speedUpUnits);
  }
  return modifiedText;
};

export const setSavings = (html, card) => {
  if (html) {
    let modifiedText = html;
    if (html.includes("savings}}")) {
      modifiedText = html.replace("{{savings}}", card.savings);
    }
    return modifiedText;
  } else {
    return null;
  }
};

export const useWrapRequest = () => {
  const setShowOverlay = useSetRecoilState(overlayState);
  const [toast, setToast] = useRecoilState(toastState);
  return async (handler, options = {}) => {
    setShowOverlay(true);
    try {
      await handler();
    } catch (error) {
      console.error(error);
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message: `Error: ${error}`,
      });
    } finally {
      setShowOverlay(false);
      if (options.afterFinally) {
        options.afterFinally();
      }
    }
  };
};

export const formatPhoneNumber = (phoneNumber) => {
  let text = phoneNumber.replace(/\D/g, "");

  // Format as (XXX) XXX-XXXX
  if (text.length > 3) {
    text = text.replace(/^(\d{3})(\d{0,3})/, "($1) $2");
  }

  // Insert hyphen after the third group of digits
  if (text.length > 6 && text.charAt(6) !== "-") {
    text = text.replace(/^(\(\d{3}\)) (\d{3})/, "$1 $2-");
  }

  // Handle backspacing after hyphen
  if (text.length < 11 && text.charAt(9) === "-") {
    text = text.substring(0, 9) + text.substring(10); // Remove the hyphen
  }

  text = text.substring(0, 14);

  return text;
};

export const formatDateOfBirth = (dob) => {
  let text = dob.replace(/\D/g, ""); // Remove non-digit characters

  // Format as MM-DD-YYYY
  if (text.length > 2) {
    text = text.substring(0, 2) + "-" + text.substring(2); // Add hyphen after month
  }
  if (text.length > 5) {
    text = text.substring(0, 5) + "-" + text.substring(5); // Add hyphen after day
  }

  // Ensure maximum length of 10 characters
  text = text.substring(0, 10);

  return text;
};

export const formatLastFourSSN = (ssn) => {
  return ssn.replace(/\D/g, "");
};

export const removeAlphaCharacters = (string) => {
  return string.replace(/\D/g, ""); // Remove non-digit characters
};

export const formatSSN = (ssn) => {
  let text = ssn.replace(/\D/g, ""); // Remove non-digit characters

  // Format as XXX-XX-XXXX
  if (text.length > 3) {
    text = text.substring(0, 3) + "-" + text.substring(3); // Add hyphen after the first three digits
  }
  if (text.length > 6) {
    text = text.substring(0, 6) + "-" + text.substring(6); // Add hyphen after the next two digits
  }

  // Ensure maximum length of 11 characters
  text = text.substring(0, 11);

  return text;
};

export const isValidFebruaryDate = (dateString) => {
  const [month, day] = dateString.split("-");
  const dayNum = parseInt(day, 10);

  if (month === "02" && (dayNum === 30 || dayNum === 31)) {
    return false;
  }
  return true;
};

export const personIsEighteenYearsOfAge = (birthDateString) => {
  // Parse the user's birth date (assuming format MM-DD-YYYY)
  const [month, day, year] = birthDateString.split("-");
  const birthDate = new Date(`${year}-${month}-${day}`);

  // Get today's date
  const today = new Date();

  // Calculate the date 18 years ago
  const eighteenYearsAgo = new Date(today.setFullYear(today.getFullYear() - 18));

  // Check if the birth date is before or the same as 18 years ago
  const isEighteen = birthDate <= eighteenYearsAgo;

  return isEighteen;
};

export const dateOfBirthValidator = (dateStr) => {
  // Regular expression to match date format (mm-dd-yyyy)
  var dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(19\d{2}|20\d{2})$/;

  // Check if the date string matches the expected format
  if (!dateRegex.test(dateStr)) {
    return false;
  }

  // Parse the date string
  var parts = dateStr.split("-");
  var month = parseInt(parts[0], 10);
  var day = parseInt(parts[1], 10);
  var year = parseInt(parts[2], 10);

  // Create a Date object and check if it represents a valid date
  var dateObj = new Date(year, month - 1, day);
  if (isNaN(dateObj.getTime())) {
    return false;
  }

  // Check if the year is greater than 1900
  if (year > 1900 && personIsEighteenYearsOfAge(dateStr) && isValidFebruaryDate(dateStr)) {
    return true;
  } else {
    return false;
  }
};

export const isValidEmail = (email) => {
  // Regular expression to check for "@" and "."
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test if the email matches the regular expression
  return emailRegex.test(email);
};

export const formatDateToYYYYMMDD = (dateString) => {
  const parts = dateString.split("-"); // Split the string into parts
  const rearrangedDateString = parts[2] + parts[0] + parts[1]; // Rearrange the parts
  return rearrangedDateString;
};

export const formatDateToMMDDYYYY = (dateString) => {
  const formattedDOB = dateString.substring(4, 6) + "-" + dateString.substring(6, 8) + "-" + dateString.substring(0, 4);
  return formattedDOB;
};

export const removeParenthesisAndDashes = (string) => {
  const cleanedString = string.replace(/\(|\)|-| /g, "");
  return cleanedString;
};

export const formatDateISOStringToMMDDYYYY = (date) => {
  const originalDate = new Date(date);
  const formattedDate = originalDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formattedDate;
};

export const formatZipCode = (zipCode) => {
  let text = zipCode.replace(/\D/g, ""); // Remove non-digit characters

  // Insert hyphen after the first five digits if there are more than five digits
  if (text.length > 5) {
    // Format Zip Code as XXXXX-XXXX
    text = text.substring(0, 5) + "-" + text.substring(5);
  }

  // Ensure maximum length of 10 characters
  text = text.substring(0, 10);

  return text;
};

export const formatEmailAddress = (email) => {
  // Regular expression to allow only alphanumeric characters, @, and .
  var pattern = /^[a-zA-Z0-9._@]+$/;

  // Test if the input matches the pattern
  if (!pattern.test(email)) {
    // If input doesn't match the pattern, remove the last entered character
    return email.slice(0, -1);
  } else {
    return email;
  }
};

export const extractPOBoxNumber = (address) => {
  const poBoxRegex = /P(?:ost)?\.?\s*(?:O(?:ffice)?)?\s*Box\s*(\d+)/i;
  const match = address.match(poBoxRegex);
  return match ? match[1] : null;
};

export const separateStreetNumber = (address) => {
  return address.match(/^\d+/);
};

export const separateStreetName = (address) => {
  return address.replace(/^\d+\s*/, "");
};

export const extractPoBoxNumberFromSelectedAddress = (address) => {
  const poBoxMatch = address.match(/Po Box (\d+)/);
  const poBoxNumber = poBoxMatch ? poBoxMatch[1] : null;

  return poBoxNumber;
};

export const getStreetNameFromSelectedAddress = (address) => {
  const streetName = address.replace(/^\d+\s*/, "");
  return streetName;
};

export const getCityFromSelectedAddress = (address) => {
  const cityMatch = address.match(/^([^,]+),/);
  return cityMatch ? cityMatch[1].trim() : null;
};

export const getStateFromSelectedAddress = (address) => {
  const stateMatch = address.match(/, (\w{2}) \d{5}$/);
  return stateMatch ? stateMatch[1] : null;
};

export const getZipCodeFromSelectedAddress = (address) => {
  const zipMatch = address.match(/\b\d{5}\b$/);
  return zipMatch ? zipMatch[0] : null;
};

export const deleteAllShopCookies = () => {
  document.cookie.split(";").forEach((cookie) => {
    const cookieName = cookie.split("=")[0].trim();
    document.cookie = `${cookieName}=; path=/; domain=comporium.com; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
};

export const getNextBillCycleDate = (billCycle) => {
  // Map bill cycle numbers to days of the month
  const billCycleDays = {
    1: 1,
    2: 8,
    3: 16,
    4: 23,
  };

  const targetDay = billCycleDays[billCycle];
  if (!targetDay) {
    throw new Error("Invalid bill cycle");
  }

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-indexed (0 = Jan)

  // Create the bill date for this month
  let billDate = new Date(currentYear, currentMonth, targetDay);

  // If the bill date has already passed, move to next month
  if (billDate <= today) {
    billDate = new Date(currentYear, currentMonth + 1, targetDay);
  }

  // Format as MM/DD/YYYY
  const formatted = `${String(billDate.getMonth() + 1).padStart(2, "0")}/${String(billDate.getDate()).padStart(2, "0")}/${billDate.getFullYear()}`;

  return formatted;
};

export const formatCancelDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  const formatted = `${year}-${month}-${day}`;

  return formatted;
};
